import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { api, ApiError, type User } from './api.ts'

interface AuthValue {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const { user: current } = await api.auth.me()
      setUser(current)
    } catch (error) {
      // 401 při startu je běžný stav — uživatel prostě není přihlášený.
      if (!(error instanceof ApiError && error.status === 401)) console.error(error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(async (email: string, password: string) => {
    const { user: logged } = await api.auth.login(email, password)
    setUser(logged)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.auth.logout()
    } finally {
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthValue>(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthValue {
  const value = use(AuthContext)
  if (!value) throw new Error('useAuth musí být uvnitř <AuthProvider>')
  return value
}

/** Je uživatel trenér nebo administrátor? */
export function isStaff(user: User | null): boolean {
  return user?.role === 'coach' || user?.role === 'admin'
}
